import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import { FiCalendar, FiCheckSquare, FiAward, FiMessageSquare, FiPlus, FiBarChart2, FiFileText, FiClock } from 'react-icons/fi';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PRIORITIES = ['High', 'Medium', 'Low'];
const EVAL_CRITERIA = ['Technical Skills', 'Communication', 'Punctuality', 'Initiative', 'Teamwork', 'Learning Speed'];

export default function Operations() {
  const { candidates, operations, updateAttendance, addTask, updateTask, addJournalEntry, addEvaluation, addWeeklyReport } = usePlatform();
  const [activeTab, setActiveTab] = useState('attendance');
  const [selectedIntern, setSelectedIntern] = useState(null);
  const [taskModal, setTaskModal] = useState(false);
  const [evalModal, setEvalModal] = useState(null);
  const [journalModal, setJournalModal] = useState(null);
  const [reportModal, setReportModal] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', priority: 'Medium', dueDate: '' });
  const [journalEntry, setJournalEntry] = useState('');
  const [evalScores, setEvalScores] = useState({});
  const [reportText, setReportText] = useState('');

  const internshipCandidates = candidates;
  const currentIntern = selectedIntern || internshipCandidates[0];
  const attendance = operations.attendance || {};
  const tasks = operations.tasks || {};
  const journals = operations.journals || {};
  const evaluations = operations.evaluations || {};
  const weeklyReports = operations.weeklyReports || {};

  const internAttendance = currentIntern ? (attendance[currentIntern.id] || {}) : {};
  const internTasks = currentIntern ? (tasks[currentIntern.id] || []) : [];
  const internJournal = currentIntern ? (journals[currentIntern.id] || []) : [];
  const internEvals = currentIntern ? (evaluations[currentIntern.id] || []) : [];

  const currentMonth = new Date();
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();

  const markAttendance = (day, status) => {
    if (!currentIntern) return;
    const dateKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    updateAttendance(currentIntern.id, dateKey, status);
    toast.success(`Marked ${status} for Day ${day}`);
  };

  const getDayAttendance = (day) => {
    const dateKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return internAttendance[dateKey];
  };

  const getAttendanceStats = () => {
    const vals = Object.values(internAttendance);
    return {
      present: vals.filter(v => v === 'present').length,
      absent: vals.filter(v => v === 'absent').length,
      halfday: vals.filter(v => v === 'halfday').length,
    };
  };

  const submitTask = () => {
    if (!taskForm.title || !currentIntern) return;
    addTask(currentIntern.id, { id: crypto.randomUUID(), ...taskForm, status: 'pending', createdAt: new Date().toISOString(), completionPct: 0 });
    toast.success('Task assigned!');
    setTaskForm({ title: '', priority: 'Medium', dueDate: '' });
    setTaskModal(false);
  };

  const toggleTaskComplete = (taskId, completed) => {
    if (!currentIntern) return;
    updateTask(currentIntern.id, taskId, { status: completed ? 'completed' : 'in progress', completionPct: completed ? 100 : 50 });
  };

  const submitJournal = () => {
    if (!journalEntry.trim() || !currentIntern) return;
    addJournalEntry(currentIntern.id, { id: crypto.randomUUID(), content: journalEntry, createdAt: new Date().toISOString() });
    toast.success('Journal entry saved!');
    setJournalEntry('');
    setJournalModal(false);
  };

  const submitEval = () => {
    if (!currentIntern) return;
    const totalScore = Object.values(evalScores).reduce((a, b) => a + Number(b), 0);
    const avgScore = totalScore / EVAL_CRITERIA.length;
    addEvaluation(currentIntern.id, { id: crypto.randomUUID(), scores: evalScores, avgScore: avgScore.toFixed(1), createdAt: new Date().toISOString(), evaluatedBy: 'Admin' });
    toast.success(`Evaluation saved! Average: ${avgScore.toFixed(1)}/10`);
    setEvalScores({});
    setEvalModal(false);
  };

  const submitReport = () => {
    if (!reportText.trim() || !currentIntern) return;
    const weekNum = Math.ceil(new Date().getDate() / 7);
    addWeeklyReport(currentIntern.id, { id: crypto.randomUUID(), week: weekNum, content: reportText, submittedAt: new Date().toISOString() });
    toast.success(`Week ${weekNum} report submitted!`);
    setReportText('');
    setReportModal(false);
  };

  const tabs = [
    { id: 'attendance', label: 'Attendance', icon: FiCalendar },
    { id: 'tasks', label: 'Tasks', icon: FiCheckSquare },
    { id: 'journal', label: 'Journal', icon: FiMessageSquare },
    { id: 'evaluation', label: 'Evaluation', icon: FiAward },
    { id: 'reports', label: 'Weekly Reports', icon: FiFileText },
  ];

  const attStats = getAttendanceStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="page-title">Internship Operations</h1>
          <p className="page-subtitle">Attendance, tasks, journal, and performance evaluations.</p>
        </div>
        <div className="flex flex-wrap gap-1.5 bg-surface-100 p-1.5 rounded-xl">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === tab.id ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <tab.icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Intern Selector */}
        <div className="col-span-12 lg:col-span-3">
          <div className="glass-card rounded-2xl p-4">
            <div className="section-title mb-3">Select Intern</div>
            {internshipCandidates.length === 0 ? (
              <p className="text-sm text-slate-700 font-medium text-center py-4">No interns yet. Import from Candidates.</p>
            ) : (
              <div className="space-y-1">
                {internshipCandidates.map(c => (
                  <button key={c.id} onClick={() => setSelectedIntern(c)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl transition-all text-sm ${currentIntern?.id === c.id ? 'bg-sky-50 text-sky-700 font-bold border border-sky-100' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <div className="font-bold truncate">{c.name}</div>
                    <div className="text-xs text-slate-800 font-semibold truncate">{c.role || c.department || 'Intern'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="col-span-12 lg:col-span-9">
          <AnimatePresence mode="wait">
            {/* ATTENDANCE */}
            {activeTab === 'attendance' && (
              <motion.div key="att" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="glass-card rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-navy-900">Daily Attendance — {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
                  {currentIntern && (
                    <div className="flex gap-3 text-sm">
                      <span className="text-emerald-600 font-bold">{attStats.present} Present</span>
                      <span className="text-rose-600 font-bold">{attStats.absent} Absent</span>
                      <span className="text-amber-600 font-bold">{attStats.halfday} Half-Day</span>
                    </div>
                  )}
                </div>
                {!currentIntern ? <p className="text-slate-400 text-center py-8">Select an intern to view attendance.</p> : (
                  <div className="grid grid-cols-7 gap-2">
                    {DAYS.map(d => <div key={d} className="text-[10px] font-black text-slate-400 text-center py-1 uppercase">{d}</div>)}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const status = getDayAttendance(day);
                      const isWeekend = (i % 7 === 5 || i % 7 === 6);
                      return (
                        <div key={day} className="relative group">
                          <div
                            onClick={() => !isWeekend && markAttendance(day, status === 'present' ? 'absent' : status === 'absent' ? 'halfday' : 'present')}
                            className={`aspect-square rounded-xl flex flex-col items-center justify-center border text-sm font-bold transition-all cursor-pointer ${
                              isWeekend ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-default' :
                              status === 'present' ? 'bg-emerald-100 border-emerald-200 text-emerald-700' :
                              status === 'absent' ? 'bg-rose-100 border-rose-200 text-rose-700' :
                              status === 'halfday' ? 'bg-amber-100 border-amber-200 text-amber-700' :
                              'bg-white border-slate-200 text-slate-400 hover:border-sky-300 hover:bg-sky-50'
                            }`}
                          >
                            {day}
                          </div>
                          {!isWeekend && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:flex gap-0.5 bg-white shadow-xl rounded-lg border border-slate-100 p-1 z-10">
                              {['present', 'absent', 'halfday'].map(s => (
                                <button key={s} onClick={() => markAttendance(day, s)} className={`px-2 py-1 text-[8px] font-black uppercase rounded ${s === 'present' ? 'bg-emerald-500 text-white' : s === 'absent' ? 'bg-rose-500 text-white' : 'bg-amber-500 text-white'}`}>{s[0]}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            {/* TASKS */}
            {activeTab === 'tasks' && (
              <motion.div key="tasks" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="glass-card rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-navy-900">Task Tracking</h2>
                  <button onClick={() => setTaskModal(true)} className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-xl text-xs font-bold hover:bg-sky-500 transition-all">
                    <FiPlus /> Assign Task
                  </button>
                </div>
                {!currentIntern ? <p className="text-slate-400 text-center py-8">Select an intern first.</p>
                  : internTasks.length === 0 ? <p className="text-slate-400 text-center py-8">No tasks assigned yet.</p>
                  : (
                    <div className="space-y-3">
                      {internTasks.map(task => (
                        <div key={task.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                          <input type="checkbox" checked={task.status === 'completed'} onChange={e => toggleTaskComplete(task.id, e.target.checked)} className="w-5 h-5 accent-sky-600 rounded" />
                          <div className="flex-1 min-w-0">
                            <div className={`font-bold text-slate-800 ${task.status === 'completed' ? 'line-through text-slate-400' : ''}`}>{task.title}</div>
                            <div className="flex gap-3 mt-1">
                              <span className={`text-[10px] font-black uppercase ${task.priority === 'High' ? 'text-rose-500' : task.priority === 'Medium' ? 'text-amber-500' : 'text-emerald-500'}`}>{task.priority}</span>
                              {task.dueDate && <span className="text-[10px] text-slate-700 font-bold font-bold flex items-center gap-1"><FiClock className="w-3 h-3" />Due: {new Date(task.dueDate).toLocaleDateString()}</span>}
                            </div>
                            <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${task.completionPct || 0}%` }} />
                            </div>
                          </div>
                          <StatusBadge status={task.status} />
                        </div>
                      ))}
                    </div>
                  )}
              </motion.div>
            )}

            {/* JOURNAL */}
            {activeTab === 'journal' && (
              <motion.div key="journal" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="glass-card rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-navy-900">Intern Journal</h2>
                  <button onClick={() => setJournalModal(true)} className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-xs font-bold hover:bg-violet-500 transition-all">
                    <FiPlus /> New Entry
                  </button>
                </div>
                {!currentIntern ? <p className="text-slate-400 text-center py-8">Select an intern first.</p>
                  : internJournal.length === 0 ? <p className="text-slate-400 text-center py-8">No journal entries yet.</p>
                  : (
                    <div className="space-y-4">
                      {[...internJournal].reverse().map(entry => (
                        <div key={entry.id} className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                          <div className="section-title mb-2">{new Date(entry.createdAt).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                          <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">{entry.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
              </motion.div>
            )}

            {/* EVALUATION */}
            {activeTab === 'evaluation' && (
              <motion.div key="eval" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="glass-card rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-navy-900">Performance Evaluation</h2>
                  <button onClick={() => setEvalModal(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-400 transition-all">
                    <FiAward className="w-3.5 h-3.5" /> Evaluate
                  </button>
                </div>
                {!currentIntern ? <p className="text-slate-400 text-center py-8">Select an intern first.</p>
                  : internEvals.length === 0 ? <p className="text-slate-400 text-center py-8">No evaluations yet.</p>
                  : internEvals.map(ev => (
                    <div key={ev.id} className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                      <div className="flex justify-between mb-4">
                        <div className="section-title">{new Date(ev.createdAt).toLocaleDateString()}</div>
                        <div className="text-2xl font-semibold text-brand-600">{ev.avgScore}<span className="text-sm text-slate-700 font-medium">/10</span></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {EVAL_CRITERIA.map(c => (
                          <div key={c} className="flex justify-between items-center text-sm">
                            <span className="text-slate-600">{c}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-slate-200 rounded-full">
                                <div className="h-full bg-sky-500 rounded-full" style={{ width: `${((ev.scores[c] || 0) / 10) * 100}%` }} />
                              </div>
                              <span className="font-bold text-slate-800 w-4">{ev.scores[c] || 0}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </motion.div>
            )}

            {/* WEEKLY REPORTS */}
            {activeTab === 'reports' && (
              <motion.div key="reports" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="glass-card rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-navy-900">Weekly Reports</h2>
                  <button onClick={() => setReportModal(true)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-500 transition-all">
                    <FiPlus /> Submit Report
                  </button>
                </div>
                {!currentIntern ? <p className="text-slate-400 text-center py-8">Select an intern first.</p> : (
                  <div className="space-y-3">
                    {(weeklyReports[currentIntern.id] || []).length === 0 ? <p className="text-slate-400 text-center py-8">No weekly reports submitted yet.</p>
                      : [...(weeklyReports[currentIntern.id] || [])].reverse().map(r => (
                        <div key={r.id} className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                          <div className="flex justify-between mb-2">
                            <span className="font-semibold text-brand-600">Week {r.week}</span>
                            <span className="text-[10px] text-slate-700 font-bold font-bold">{new Date(r.submittedAt).toLocaleDateString()}</span>
                          </div>
                          <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">{r.content}</p>
                        </div>
                      ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Task Modal */}
      <Modal isOpen={taskModal} onClose={() => setTaskModal(false)} title="Assign Task">
        <div className="p-6 space-y-4">
          <div>
            <label className="block section-title mb-2">Task Title</label>
            <input value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block section-title mb-2">Priority</label>
              <select value={taskForm.priority} onChange={e => setTaskForm(p => ({ ...p, priority: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400 bg-white">
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block section-title mb-2">Due Date</label>
              <input type="date" value={taskForm.dueDate} onChange={e => setTaskForm(p => ({ ...p, dueDate: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setTaskModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
            <button onClick={submitTask} className="px-8 py-2.5 bg-sky-600 text-white rounded-xl font-bold text-sm hover:bg-sky-500 transition-all">Assign</button>
          </div>
        </div>
      </Modal>

      {/* Journal Modal */}
      <Modal isOpen={journalModal} onClose={() => setJournalModal(false)} title="New Journal Entry">
        <div className="p-6 space-y-4">
          <textarea value={journalEntry} onChange={e => setJournalEntry(e.target.value)} rows={8} placeholder="Write about today's work, learnings, and challenges..." className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-sky-400 resize-none" />
          <div className="flex justify-end gap-3">
            <button onClick={() => setJournalModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
            <button onClick={submitJournal} className="px-8 py-2.5 bg-violet-600 text-white rounded-xl font-bold text-sm hover:bg-violet-500 transition-all">Save Entry</button>
          </div>
        </div>
      </Modal>

      {/* Evaluation Modal */}
      <Modal isOpen={evalModal} onClose={() => setEvalModal(false)} title={`Performance Evaluation — ${currentIntern?.name}`}>
        <div className="p-6 space-y-4">
          <div className="space-y-4">
            {EVAL_CRITERIA.map(criterion => (
              <div key={criterion}>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-bold text-slate-700">{criterion}</label>
                  <span className="text-sm font-semibold text-brand-600">{evalScores[criterion] || 0}/10</span>
                </div>
                <input type="range" min="0" max="10" value={evalScores[criterion] || 0}
                  onChange={e => setEvalScores(p => ({ ...p, [criterion]: parseInt(e.target.value) }))}
                  className="w-full accent-sky-600" />
              </div>
            ))}
          </div>
          <div className="p-4 bg-sky-50 rounded-2xl border border-sky-100">
            <div className="text-[10px] font-black text-sky-700 uppercase tracking-widest mb-1">Average Score</div>
            <div className="text-3xl font-semibold text-brand-600">
              {EVAL_CRITERIA.length > 0 ? (Object.values(evalScores).reduce((a, b) => a + b, 0) / EVAL_CRITERIA.length).toFixed(1) : '0.0'}<span className="text-lg text-slate-400">/10</span>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setEvalModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
            <button onClick={submitEval} className="px-8 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-400 transition-all">Save Evaluation</button>
          </div>
        </div>
      </Modal>

      {/* Weekly Report Modal */}
      <Modal isOpen={reportModal} onClose={() => setReportModal(false)} title="Submit Weekly Report">
        <div className="p-6 space-y-4">
          <textarea value={reportText} onChange={e => setReportText(e.target.value)} rows={8} placeholder="Summarize tasks completed, progress made, blockers, and goals for next week..." className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-sky-400 resize-none" />
          <div className="flex justify-end gap-3">
            <button onClick={() => setReportModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
            <button onClick={submitReport} className="px-8 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-500 transition-all">Submit Report</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}